import {
  pageOffset,
  type SortTableId,
  type TableSort,
  type TaxEntryAward,
  type TaxEntryLine,
} from '@myrewards/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Link } from 'react-aria-components';
import { isApiError } from '../../api/errors';
import { letterKeys } from '../../api/letters';
import { checkKeys, queueKeys, saveTaxEntry, taxEntryKeys } from '../../api/ops';
import { waitingQuery } from '../../api/server';
import {
  Button,
  clientPage,
  cx,
  FilterBar,
  formatDate,
  formatMoney,
  Pager,
  SortByMenu,
  SortHead,
  StatusPill,
  tableCards,
  useCardRows,
  useClampedPage,
  useListTable,
  usePaging,
  useScrollRegion,
  useToast,
  type TableCards,
} from '../../ui';
import { isStale, problemWords } from '../labels';
import o from '../Ops.module.css';
import { NoMatches } from '../queue/NoMatches';
import { AmountInput, AttachButton, Notice } from '../shared';
import {
  canSave,
  enteredWords,
  taxDraftFrom,
  typeNetPay,
  typeTaxes,
  type TaxDraft,
  sortTaxAwards,
  TAX_DEFAULT_SORT,
  TAX_DEFAULT_WORDS,
  TAX_SORT_COLUMNS,
  taxFilters,
} from './presenter';
import s from './Tax.module.css';

const COLUMNS = 6;

/** What a refused save says, in the row it happened in. */
function saveProblem(error: unknown): string {
  if (isApiError(error))
    switch (error.code) {
      case 'tax_changed':
        return 'This changed since you opened it: check the figure again.';
      case 'letters_released':
        return 'The letters are out: the figure on the letter stands, and Payroll sets the final tax.';
      case 'tax_above_cash':
        return 'Taxes can’t be more than the gross.';
    }
  return problemWords(error);
}

function TaxRow({
  award,
  line,
  c,
  problem,
  saving,
  takeFocus,
  onFocused,
  onSave,
  onUploaded,
}: {
  award: TaxEntryAward;
  line: TaxEntryLine;
  c: TableCards;
  problem: string | null;
  saving: boolean;
  /** Just saved: the keyboard comes back to this row's Change. */
  takeFocus: boolean;
  onFocused: () => void;
  onSave: (taxCents: number) => void;
  onUploaded: () => void;
}): ReactElement {
  const [draft, setDraft] = useState<TaxDraft>(() => taxDraftFrom(line.cashCents, line.taxCents));
  const [changing, setChanging] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const taxesRef = useRef<HTMLInputElement>(null);
  const netRef = useRef<HTMLInputElement>(null);
  const changeRef = useRef<HTMLButtonElement>(null);
  const problemId = useId();
  const editable = line.canChange && (line.taxCents === null || changing);
  const entered = enteredWords(line);
  const name = line.name;
  // The box the problem came from is the one marked wrong, with the reason under it.
  const wrong = draft.problem === null ? null : draft.edited === 'net' ? 'net' : 'tax';
  const said = draft.problem ?? hint;

  const backToChange = useRef(false);
  useEffect(() => {
    if (changing) taxesRef.current?.focus();
    else if (backToChange.current) {
      backToChange.current = false;
      changeRef.current?.focus();
    }
  }, [changing]);
  useEffect(() => {
    if (!takeFocus || changeRef.current === null) return;
    changeRef.current.focus();
    onFocused();
  }, [takeFocus, onFocused]);

  /** Checked when pressed, so a figure typed and clicked straight away saves in one click. */
  const save = (): void => {
    if (draft.problem !== null) {
      (wrong === 'net' ? netRef : taxesRef).current?.focus();
      return;
    }
    if (draft.taxCents === null) {
      setHint('Type the taxes or the net pay first.');
      taxesRef.current?.focus();
      return;
    }
    if (!canSave(draft, line.taxCents)) {
      // The same figure as saved: nothing to send, and the keyboard goes back to Change.
      backToChange.current = true;
      setChanging(false);
      return;
    }
    onSave(draft.taxCents);
  };

  const message = (box: 'tax' | 'net') =>
    said !== null && (wrong ?? 'tax') === box ? (
      <span id={problemId} role="alert" className={s.fieldProblem}>
        {said}
      </span>
    ) : null;

  return (
    <tr {...c.row}>
      <td {...c.cell('Person', 'title')}>
        <span className={o.strong}>{name}</span>
        {line.eeNumber !== null && <span className={o.sub}>EE# {line.eeNumber}</span>}
      </td>
      <td {...c.cell('Gross', 'value', s.num)}>
        {formatMoney(line.cashCents)}
        <span className={o.sub}>Cash on the award</span>
      </td>
      <td {...c.cell('Taxes', 'value', s.num)}>
        {editable ? (
          <AmountInput
            label={`Taxes for ${name}`}
            valueCents={draft.taxCents}
            onChange={(cents) => {
              setHint(null);
              setDraft((d) => typeTaxes(d, line.cashCents, cents));
            }}
            wholeDollars={false}
            isInvalid={wrong === 'tax'}
            {...(said !== null && (wrong ?? 'tax') === 'tax' && { describedBy: problemId })}
            inputRef={taxesRef}
          />
        ) : (
          <span className={s.figure}>
            {line.taxCents === null ? 'Not entered yet' : formatMoney(line.taxCents)}
          </span>
        )}
        {editable && draft.edited === 'net' && draft.problem === null && (
          <span className={o.sub}>Worked out from the net pay</span>
        )}
        {editable && message('tax')}
      </td>
      <td {...c.cell('Net pay', 'value', s.num)}>
        {editable ? (
          <AmountInput
            label={`Net pay for ${name}`}
            valueCents={draft.netPayCents}
            onChange={(cents) => {
              setHint(null);
              setDraft((d) => typeNetPay(d, line.cashCents, cents));
            }}
            wholeDollars={false}
            isInvalid={wrong === 'net'}
            {...(wrong === 'net' && { describedBy: problemId })}
            inputRef={netRef}
          />
        ) : (
          <span className={s.figure}>
            {line.taxCents === null
              ? 'After the taxes'
              : formatMoney(line.cashCents - line.taxCents)}
          </span>
        )}
        {editable && draft.edited === 'tax' && draft.problem === null && (
          <span className={o.sub}>Worked out · you can type over it</span>
        )}
        {editable && message('net')}
      </td>
      <td {...c.cell('ADP screenshot')}>
        {line.screenshots > 0 && <span className={o.sub}>ADP screenshot attached</span>}
        {/* Once the figure can't change, an empty cell would read as missing: say none came. */}
        {line.screenshots === 0 && !line.canChange && <span className={o.sub}>No screenshot</span>}
        {line.canChange && (
          <AttachButton
            requestId={award.award.id}
            purpose="tax_estimate"
            lineId={line.lineId}
            staffOnly
            label={line.screenshots > 0 ? 'Attach another' : 'Attach'}
            accessibleLabel={`Attach the ADP screenshot for ${name}`}
            onUploaded={onUploaded}
          />
        )}
      </td>
      <td {...c.cell('Status', 'action')}>
        <div className={s.statusCell}>
          {line.taxCents === null ? (
            <StatusPill size="sm" tone="waiting" label="To enter" />
          ) : (
            <span className={s.pills}>
              <StatusPill
                size="sm"
                tone={award.lettersOut ? 'neutral' : 'done'}
                label={award.lettersOut ? 'On the letter' : 'Entered'}
              />
              <StatusPill size="sm" tone="waiting" label="Estimate" />
            </span>
          )}
          {entered !== null && <span className={o.sub}>{entered}</span>}
          {editable && (
            <Button
              size="sm"
              variant="navy"
              isPending={saving}
              aria-label={`Save the tax for ${name}`}
              onPress={save}
            >
              Save
            </Button>
          )}
          {!editable && line.canChange && line.taxCents !== null && (
            <Button
              ref={changeRef}
              size="sm"
              variant="ghost"
              aria-label={`Change the tax for ${name}`}
              onPress={() => {
                setChanging(true);
              }}
            >
              Change
            </Button>
          )}
          {problem !== null && <Notice tone="problem">{problem}</Notice>}
        </div>
      </td>
    </tr>
  );
}

/** Saves by row, with each row's refusal kept by person so it survives a refetch. */
export interface TaxSaver {
  readonly problems: Readonly<Record<string, string>>;
  /** Rows whose save is on its way: each stays busy until its own save and refetch are done. */
  readonly savingLineIds: ReadonlySet<string>;
  /** The row just saved, whose Change takes the keyboard once it shows. */
  readonly focusLineId: string | null;
  readonly focused: () => void;
  save(award: TaxEntryAward, line: TaxEntryLine, taxCents: number): void;
  refresh(award: TaxEntryAward): Promise<void>;
}

/**
 * Saving a row (APP-101 §5): the screen's gross and figure go with it, a refusal stays on that
 * person's row, and a conflict reloads the list so the newer figure shows.
 */
export function useTaxSaver(): TaxSaver {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [problems, setProblems] = useState<Record<string, string>>({});
  const [focusLineId, setFocusLineId] = useState<string | null>(null);
  // By row, not the latest call: a row saved while another is on its way stays busy, so it
  // can't be sent again with the figure it had before its first save landed.
  const inFlight = useRef(new Set<string>());
  const [savingLineIds, setSavingLineIds] = useState<ReadonlySet<string>>(() => new Set());
  const settle = (lineId: string, on: boolean) => {
    if (on) inFlight.current.add(lineId);
    else inFlight.current.delete(lineId);
    setSavingLineIds(new Set(inFlight.current));
  };
  const refresh = async (award: TaxEntryAward) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: taxEntryKeys.all }),
      queryClient.invalidateQueries({ queryKey: queueKeys.all }),
      queryClient.invalidateQueries({ queryKey: waitingQuery.queryKey }),
      queryClient.invalidateQueries({ queryKey: checkKeys.one(award.award.id) }),
      queryClient.invalidateQueries({ queryKey: letterKeys.one(award.award.id) }),
    ]);
  };
  const mutation = useMutation({
    mutationFn: (v: { award: TaxEntryAward; line: TaxEntryLine; taxCents: number }) =>
      saveTaxEntry(v.line.lineId, {
        cashCents: v.line.cashCents,
        previousTaxCents: v.line.taxCents,
        taxCents: v.taxCents,
      }),
    onSuccess: async (_, v) => {
      setProblems((p) =>
        Object.fromEntries(Object.entries(p).filter(([lineId]) => lineId !== v.line.lineId)),
      );
      await refresh(v.award);
      setFocusLineId(v.line.lineId);
      toast.show({
        tone: 'done',
        title: `${v.line.name}’s tax saved`,
        body: `${formatMoney(v.taxCents)} taxes on ${formatMoney(v.line.cashCents)}. It’s an estimate: Payroll sets the final tax.`,
      });
    },
    onError: async (e, v) => {
      const words = saveProblem(e);
      setProblems((p) => ({ ...p, [v.line.lineId]: words }));
      // Said in a message too: a row that has left the list (sent back) can't show it.
      toast.show({ tone: 'problem', title: `${v.line.name}’s tax wasn’t saved`, body: words });
      if (isStale(e) || (isApiError(e) && e.code === 'letters_released')) await refresh(v.award);
    },
    onSettled: (_, __, v) => {
      settle(v.line.lineId, false);
    },
  });
  const focused = useCallback(() => {
    setFocusLineId(null);
  }, []);
  return {
    problems,
    focusLineId,
    focused,
    savingLineIds,
    save: (award, line, taxCents) => {
      if (inFlight.current.has(line.lineId)) return;
      settle(line.lineId, true);
      mutation.mutate({ award, line, taxCents });
    },
    refresh,
  };
}

/**
 * The tax rows (APP-101): one PAA per group, one person per row, taxes and net pay linked, each
 * row saved on its own. The Tax to enter list, the check and Letters all use it, so a figure looks
 * and saves the same everywhere.
 */
export function TaxEntryTable({
  awards,
  caption,
  showAward = true,
  saver,
  emptyState,
  note = true,
  sorting,
}: {
  awards: readonly TaxEntryAward[];
  caption: string;
  /** Group rows by award with its number and link (the list); off on one award's own page. */
  showAward?: boolean;
  /** Shared by the tables on one page, so a row's refusal survives its award moving section. */
  saver?: TaxSaver;
  /**
   * With no awards, the header row stays and this designed empty state is its one row (APP-093,
   * DESIGN-SPEC §5).
   */
  emptyState?: ReactNode;
  /** The ADP Quick Calc how-to under the table; off for a second table that would repeat it. */
  note?: boolean;
  /**
   * Sorting by the headings (APP-149 D3), on the Tax to enter list; `awards` are the whole list,
   * for the Sort by menu's words. Left out (the check, Letters), the headings are words only.
   */
  sorting?: {
    sort: TableSort;
    onSortChange: (next: TableSort) => void;
    awards: readonly TaxEntryAward[];
  };
}): ReactElement {
  const own = useTaxSaver();
  const saving = saver ?? own;
  const c = tableCards(useCardRows());
  /** A heading; with sorting on, a button that sorts by it. A function, so focus stays. */
  const heading = (id: string, words: string, numeric = false): ReactElement =>
    sorting === undefined ? (
      <th scope="col" className={numeric ? s.num : undefined} {...c.header}>
        {words}
      </th>
    ) : (
      <SortHead
        column={id}
        sort={sorting.sort}
        onSortChange={sorting.onSortChange}
        numeric={numeric}
        plain={c.on}
        pad={10}
        className={numeric ? s.num : undefined}
        {...c.header}
      >
        {words}
      </SortHead>
    );
  const scrollRef = useRef<HTMLDivElement>(null);
  const region = useScrollRegion(scrollRef, caption);
  return (
    <div ref={scrollRef} className={s.tableScroll} {...(c.on ? {} : region)}>
      {/* Card rows have no headings to press: the Sort by menu takes their place. */}
      {c.on && sorting !== undefined && (
        <SortByMenu
          columns={TAX_SORT_COLUMNS.filter((column) => column.id !== 'approved')}
          rows={sorting.awards}
          sort={sorting.sort}
          onSortChange={sorting.onSortChange}
        />
      )}
      <table {...c.table(s.table)}>
        <caption className="visually-hidden">{caption}</caption>
        <thead {...c.head}>
          <tr {...c.row}>
            {heading('person', 'Person')}
            {heading('gross', 'Gross', true)}
            {heading('taxes', 'Taxes', true)}
            {heading('net', 'Net pay', true)}
            <th scope="col" {...c.header}>
              ADP screenshot
            </th>
            {heading('status', 'Status')}
          </tr>
        </thead>
        {awards.length === 0 && emptyState !== undefined && (
          <tbody {...c.body}>
            <tr className={s.emptyRow} {...c.row}>
              <td colSpan={COLUMNS} {...c.wide}>
                {emptyState}
              </td>
            </tr>
          </tbody>
        )}
        {awards.map((award) => (
          <tbody key={award.award.id} {...c.body}>
            {showAward && (
              <tr className={s.groupRow} {...c.row}>
                <th scope="rowgroup" colSpan={COLUMNS} {...c.groupHeader}>
                  <Link href={`/awards/${award.award.id}`} className={o.strongLink}>
                    {award.award.displayId}
                  </Link>
                  <span className={s.groupFacts}>
                    {[
                      award.sector,
                      award.awardDate === null ? null : `award date ${formatDate(award.awardDate)}`,
                      award.fmvCents === null ? null : `FMV ${formatMoney(award.fmvCents)}`,
                      award.awardScreenshots === 0
                        ? null
                        : award.awardScreenshots === 1
                          ? 'an ADP screenshot for the whole award'
                          : `${String(award.awardScreenshots)} ADP screenshots for the whole award`,
                      award.toEnter === 0
                        ? 'everyone entered'
                        : `the letters wait on ${String(award.toEnter)} more`,
                    ]
                      .filter((p): p is string => p !== null)
                      .join(' · ')}
                  </span>
                </th>
              </tr>
            )}
            {award.people.map((line) => (
              <TaxRow
                // A new gross or a new saved figure starts the row again: a draft typed against
                // the old one can't be saved by a second click (APP-101 §5).
                key={`${line.lineId}:${String(line.cashCents)}:${String(line.taxCents)}`}
                award={award}
                line={line}
                c={c}
                problem={saving.problems[line.lineId] ?? null}
                saving={saving.savingLineIds.has(line.lineId)}
                takeFocus={saving.focusLineId === line.lineId}
                onFocused={saving.focused}
                onSave={(taxCents) => {
                  saving.save(award, line, taxCents);
                }}
                onUploaded={() => {
                  void saving.refresh(award);
                }}
              />
            ))}
          </tbody>
        ))}
      </table>
      {note && (
        <p className={cx(o.muted, s.note)}>
          Run ADP Quick Calc as a supplemental wage bonus, earnings code “bonus”, no deductions.
          Type the taxes and the net pay follows, or type over the net pay. Payroll sets the final
          tax.
        </p>
      )}
    </div>
  );
}

/**
 * The Tax to enter list, one page of people at a time (APP-135): the list is whole on the screen,
 * so the page is cut here. `prefix` names this table's params in the address when a page holds
 * two (`enteredPage`, `enteredSort`). It sorts and filters by sector over the whole list first
 * (APP-149); `table` is the saved sort's key, or null for none.
 */
export function PagedTaxEntryTable({
  prefix,
  table: sortKey,
  ...table
}: {
  prefix?: string;
  table: SortTableId | null;
} & Omit<Parameters<typeof TaxEntryTable>[0], 'sorting'>): ReactElement {
  const param = prefix === undefined ? 'page' : `${prefix}Page`;
  const barRef = useRef<HTMLDivElement>(null);
  const filters = useMemo(() => taxFilters(table.awards), [table.awards]);
  const list = useListTable({
    table: sortKey,
    rows: table.awards,
    columns: TAX_SORT_COLUMNS,
    defaultSort: TAX_DEFAULT_SORT,
    defaultWords: TAX_DEFAULT_WORDS,
    filters,
    order: sortTaxAwards,
    ...(prefix !== undefined && { params: { prefix } }),
    barRef,
  });
  const paging = usePaging('tax_entry', { param });
  const { anchorRef } = paging;
  // A page holds whole awards: `size` counts awards, so an award's own controls show once.
  const paged = clientPage(list.rows, paging.page, paging.size);
  const info = paged.page;
  useClampedPage(paging, info.number);
  const any = table.awards.length > 0;
  return (
    <>
      {any && (
        <div ref={barRef}>
          <FilterBar label={`Filter ${table.caption}`} {...list.bar} />
        </div>
      )}
      <section ref={anchorRef} tabIndex={-1}>
        <TaxEntryTable
          {...table}
          awards={paged.rows}
          {...(any && {
            sorting: { sort: list.sort, onSortChange: list.setSort, awards: table.awards },
          })}
          {...(any &&
            list.rows.length === 0 && {
              emptyState: <NoMatches what="PAAs" onClear={list.clearFromEmpty} />,
            })}
        />
      </section>
      <Pager
        label={table.caption}
        page={info}
        onPage={(n) => {
          paging.setPage(n);
        }}
        onSize={(size) => {
          paging.setSize(size, pageOffset(info));
        }}
      />
    </>
  );
}
